import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, MailCheck, Sparkles, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";

export default function TrialBanner() {
    const { user } = useAuth();
    const { T } = useLang();
    const [usage, setUsage] = useState(null);
    const [resending, setResending] = useState(false);

    const onTrial = !!user?.on_trial;
    useEffect(() => {
        if (onTrial) {
            api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
        }
    }, [onTrial, user?.plan_key]);

    if (!user) return null;

    async function resend() {
        setResending(true);
        try {
            const { data } = await api.post("/auth/resend-verification", {});
            toast.success(data.message || "Verification email sent.");
            if (data.dev_verify_url) {
                toast(
                    <span>
                        Didn't get the email?{" "}
                        <Link to={data.dev_verify_url} className="underline">Open your verification link</Link>
                    </span>,
                    { duration: 15000 },
                );
            }
        } catch {
            toast.error("Could not resend. Try again in a moment.");
        } finally {
            setResending(false);
        }
    }

    // 1) Verification pending
    if (!user.email_verified && (user.plan_key === "pending" || user.on_trial)) {
        return (
            <Bar tone="amber" testId="trial-banner-verify">
                <MailCheck size={15} className="shrink-0" />
                <span className="min-w-0">
                    {T("Verify your email to activate your workspace. We sent a link to", "Çalışma alanınızı etkinleştirmek için e-postanızı doğrulayın. Bağlantıyı şu adrese gönderdik:")} <strong>{user.email}</strong>.
                </span>
                <button
                    type="button"
                    onClick={resend}
                    disabled={resending}
                    className="underline underline-offset-2 shrink-0 disabled:opacity-50"
                    data-testid="trial-banner-resend"
                >
                    {resending ? T("Sending…", "Gönderiliyor…") : T("Resend link", "Bağlantıyı yeniden gönder")}
                </button>
            </Bar>
        );
    }

    // 2) Verified but no plan chosen yet
    if (user.plan_key === "pending") {
        return (
            <Bar tone="navy" testId="trial-banner-choose">
                <Sparkles size={15} className="shrink-0" color="#C9A84C" />
                <span className="min-w-0">{T("Your email is verified. Start your 7-day free trial to unlock the platform.", "E-postanız doğrulandı. Platformu açmak için 7 günlük ücretsiz denemenizi başlatın.")}</span>
                <Link to="/app/onboarding" className="underline underline-offset-2 shrink-0" data-testid="trial-banner-choose-link">
                    {T("Choose trial", "Denemeyi başlat")}
                </Link>
            </Bar>
        );
    }

    if (!user.on_trial) return null;

    const ends = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
    const daysLeft = ends ? Math.max(0, Math.ceil((ends - Date.now()) / 86400000)) : 0;
    const expired = ends && Date.now() > ends.getTime();

    // 3) Trial expired
    if (expired) {
        return (
            <Bar tone="red" testId="trial-banner-expired">
                <TriangleAlert size={15} className="shrink-0" />
                <span className="min-w-0">
                    {T("Your free trial has ended. Your saved work is preserved. Upgrade to continue researching.", "Ücretsiz denemeniz sona erdi. Kayıtlı çalışmalarınız korunuyor. Araştırmaya devam etmek için planınızı yükseltin.")}
                </span>
                <Link to="/app/pricing" className="underline underline-offset-2 shrink-0 font-medium" data-testid="trial-banner-upgrade">
                    {T("Upgrade now", "Şimdi yükselt")}
                </Link>
            </Bar>
        );
    }

    // 4) Trial active
    const used = usage?.used ?? 0;
    const limit = usage?.limit ?? 15;
    return (
        <Bar tone="navy" testId="trial-banner-active">
            <Clock size={15} className="shrink-0" color="#C9A84C" />
            <span className="min-w-0">
                {T("Free trial:", "Ücretsiz deneme:")} <strong>{daysLeft} {T(daysLeft === 1 ? "day" : "days", "gün")}</strong> {T("and", "ve")}{" "}
                <strong>{Math.max(0, limit - used)} / {limit} {T("credits", "kredi")}</strong> {T("remaining.", "kaldı.")}
            </span>
            <Link to="/app/pricing" className="underline underline-offset-2 shrink-0" data-testid="trial-banner-upgrade">
                {T("Upgrade", "Yükselt")}
            </Link>
        </Bar>
    );
}

function Bar({ tone, children, testId }) {
    const styles = {
        amber: "bg-amber-50 text-amber-950 border-amber-500/40",
        navy: "bg-[#0D1B3E] text-[#F5F0E8] border-[#C9A84C]/50",
        red: "bg-[#8B1E1E] text-[#F5F0E8] border-[#8B1E1E]",
    }[tone];
    return (
        <div
            className={`border-b px-4 lg:px-8 py-2.5 text-sm flex items-center gap-3 flex-wrap ${styles}`}
            data-testid={testId}
        >
            {children}
        </div>
    );
}
